import { useCallback, useEffect, useRef, useState } from "react";

import { assignLocation } from "@/utils/auth0/resetPasswordSuccessRedirect";

/** Soft CTA wait budget while `/redirect/resolve` is still pending after click. */
export const DEFERRED_RESOLVE_WAIT_MS = 2500;

interface UseDeferredResolveNavigateResult {
  isNavigating: boolean;
  navigate: () => void;
}

/**
 * Soft CTA leave via prefetched `/redirect/resolve`, with a short wait when
 * resolve is still pending. The deadline starts on click and is not restarted —
 * a later locale change must not extend the wait.
 */
export function useDeferredResolveNavigate(
  redirectUrl: string | null,
  resolveFailed: boolean,
  fallbackUrl: string,
): UseDeferredResolveNavigateResult {
  const [isWaiting, setIsWaiting] = useState(false);
  const hasNavigatedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const latestRef = useRef({ redirectUrl, resolveFailed, fallbackUrl });
  latestRef.current = { redirectUrl, resolveFailed, fallbackUrl };

  const clearWait = useCallback(() => {
    if (timeoutRef.current === null) {
      return;
    }
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const assign = useCallback(
    (url: string) => {
      if (hasNavigatedRef.current) {
        return;
      }
      hasNavigatedRef.current = true;
      clearWait();
      assignLocation(url);
    },
    [clearWait],
  );

  useEffect(() => () => clearWait(), [clearWait]);

  // Leave as soon as resolve finishes. The click handler owns the deadline,
  // so a later locale change does not start another wait.
  useEffect(() => {
    if (!isWaiting || hasNavigatedRef.current) {
      return;
    }

    if (redirectUrl) {
      assign(redirectUrl);
      return;
    }

    if (resolveFailed) {
      assign(fallbackUrl);
    }
  }, [assign, fallbackUrl, isWaiting, redirectUrl, resolveFailed]);

  const navigate = useCallback(() => {
    if (hasNavigatedRef.current || timeoutRef.current !== null) {
      return;
    }

    const current = latestRef.current;
    if (current.redirectUrl) {
      assign(current.redirectUrl);
      return;
    }

    if (current.resolveFailed) {
      assign(current.fallbackUrl);
      return;
    }

    setIsWaiting(true);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      const latest = latestRef.current;
      assign(latest.redirectUrl ?? latest.fallbackUrl);
    }, DEFERRED_RESOLVE_WAIT_MS);
  }, [assign]);

  return {
    isNavigating: isWaiting,
    navigate,
  };
}
